import { createContext, useContext, useEffect, useState, useMemo } from 'react'

import seedrandom from 'seedrandom'

import {
  useStateTrigger,
  useStateRef,
  useLocalStateRef,
  useStorageFoundWord // mantém import; remova se não usar
} from './hooks'

export const boardSize = 10

const minWordLength = 4
const frequencies = [
  78, // a
  20, // b
  40, // c
  38, // d
  111, // e
  14, // f
  30, // g
  23, // h
  82, // i
  2, // j
  25, // k
  53, // l
  27, // m
  72, // n
  61, // o
  28, // p
  0, // q
  74, // r
  88, // s
  68, // t
  33, // u
  1, // v
  9, // w
  3, // x
  16, // y
  4 // z
]
const wordScores = [1, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]
const GameContext = createContext()

// Direções únicas (consistentes entre geração e busca de palavras)
const DIRECTIONS = [
  [1, 0], // →
  [0, 1], // ↓
  [1, -1], // ↘ (subindo no Y)
  [1, 1] // ↘
]

const GameProvider = ({
  children,
  wordsList: _wordsList,
  onComplete,
  onError,
  ...rest
}) => {
  const wordsList = _wordsList.map(word => word.trim())

  /* helpers */

  const getRandomInt = (prng, n) => Math.floor(prng() * n)

  const clone2dArray = array => array.map(row => row.slice())

  /* seed generation */

  // Seed determinística por dia (UTC) — evita lodash/underscore
  const getTodaysSeed = () => {
    const d = new Date()
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}` // seedrandom aceita string
  }

  /* board generation */

  const generateLetter = prng => {
    // Usa o total real da distribuição
    const total = frequencies.reduce((a, b) => a + b, 0)
    const r = prng() * total
    let acc = 0
    for (let i = 0; i < frequencies.length; i++) {
      acc += frequencies[i]
      if (r < acc) {
        return String.fromCharCode(65 + i) // A-Z
      }
    }
    // fallback defensivo (deveria nunca cair aqui)
    return 'Z'
  }

  const generateBoard = prng => {
    const board = Array.from({ length: boardSize }, () =>
      Array(boardSize).fill(' ')
    )

    const isValidStartingPoint = (x, y, dx, dy, length) => {
      const endX = x + dx * (length - 1)
      const endY = y + dy * (length - 1)

      // limites (negativos e estouro)
      if (endX < 0 || endX >= boardSize) return false
      if (endY < 0 || endY >= boardSize) return false

      // checar colisão/ocupação (apenas vazios aqui; compatível com checagem fina abaixo)
      for (let c = 0; c < length; c++) {
        const xi = x + c * dx
        const yi = y + c * dy
        if (xi < 0 || yi < 0 || xi >= boardSize || yi >= boardSize) return false
        if (board[xi][yi] !== ' ') return false
      }
      return true
    }

    const placeWord = word => {
      // candidatos (x,y,dir)
      const candidates = []
      for (let x = 0; x < boardSize; x++) {
        for (let y = 0; y < boardSize; y++) {
          for (const [dx, dy] of DIRECTIONS) {
            candidates.push({ x, y, dx, dy })
          }
        }
      }

      // embaralhar candidatos de forma reprodutível
      for (let i = candidates.length - 1; i > 0; i--) {
        const j = getRandomInt(prng, i + 1)
        ;[candidates[i], candidates[j]] = [candidates[j], candidates[i]]
      }

      for (const { x: sx, y: sy, dx, dy } of candidates) {
        if (!isValidStartingPoint(sx, sy, dx, dy, word.length)) continue

        let canPlace = true
        for (let c = 0; c < word.length; c++) {
          const x = sx + c * dx
          const y = sy + c * dy
          // Permite sobrepor mesma letra (opcional e já suportado no check)
          if (board[x][y] !== ' ' && board[x][y] !== word.charAt(c)) {
            canPlace = false
            break
          }
        }
        if (!canPlace) continue

        // escreve
        for (let c = 0; c < word.length; c++) {
          const x = sx + c * dx
          const y = sy + c * dy
          board[x][y] = word.charAt(c)
        }
        return true // colocado com sucesso
      }

      // não coube — não travar
      // console.warn(`Não foi possível posicionar: ${word}`)
      return false
    }

    // coloca cada palavra (best-effort)
    for (const word of wordsList) {
      placeWord(word)
    }

    // preenche o restante com letras aleatórias
    for (let i = 0; i < boardSize; i++) {
      for (let j = 0; j < boardSize; j++) {
        if (board[i][j] === ' ') {
          board[i][j] = generateLetter(prng)
        }
      }
    }

    return board
  }

  const generateTodaysBoard = () => {
    const prng = seedrandom(getTodaysSeed())
    return generateBoard(prng)
  }

  /* finding words */

  // Mantido por compatibilidade; não é usado no fluxo principal
  const checkWord = (board, word, pos = null) => {
    if (word.length === 0) return true

    if (pos === null) {
      for (let i = 0; i < boardSize; i++) {
        for (let j = 0; j < boardSize; j++) {
          if (board[i][j] === word.charAt(0)) {
            const newBoard = clone2dArray(board)
            newBoard[i][j] = null
            if (checkWord(newBoard, word.substr(1), { x: i, y: j })) return true
          }
        }
      }
    } else {
      for (const i of [-1, 0, 1]) {
        for (const j of [-1, 0, 1]) {
          if (i === 0 && j === 0) continue
          const x = pos.x + i
          const y = pos.y + j
          if (x < 0 || y < 0 || x >= boardSize || y >= boardSize) continue
          if (board[x][y] === word.charAt(0)) {
            const newBoard = clone2dArray(board)
            newBoard[x][y] = null
            if (checkWord(newBoard, word.substr(1), { x, y })) return true
          }
        }
      }
    }

    return false
  }

  const findWordPath = (board, word, results) => {
    const size = board.length

    const isValidPosition = (x, y) => x >= 0 && y >= 0 && x < size && y < size

    const searchInDirection = (x, y, dx, dy, k) => {
      if (!isValidPosition(x, y) || board[x][y] !== word[k]) return false
      if (k === word.length - 1) return [[x, y]]

      const nx = x + dx
      const ny = y + dy
      const tail = searchInDirection(nx, ny, dx, dy, k + 1)
      if (tail) return [[x, y], ...tail]
      return false
    }

    for (let x = 0; x < size; x++) {
      for (let y = 0; y < size; y++) {
        for (const [dx, dy] of DIRECTIONS) {
          const path = searchInDirection(x, y, dx, dy, 0)
          if (path) {
            const foundWordIndex = results.findIndex(r => r.word === word)
            if (foundWordIndex !== -1) results[foundWordIndex].found = true
            return path
          }
        }
      }
    }
    return null
  }

  const findAllWords = board => {
    const results = []
    for (const word of wordsList) {
      if (word.length < minWordLength) continue
      const path = findWordPath(board, word, results)
      if (path) results.push({ word, path })
    }
    return results
  }

  /* scoring */

  const getWordScore = word => {
    let index = word.length - minWordLength
    if (index < 0) index = 0
    if (index > wordScores.length - 1) index = wordScores.length - 1
    return wordScores[index]
  }

  // state
  const [currentWord, currentWordRef, setCurrentWord] = useStateRef('')
  const [foundWords, foundWordsRef, setFoundWords] = useLocalStateRef([])
  const [wordActive, setWordActive] = useState(false)
  const [path, setPath] = useState([])
  const [wordInfo, wordInfoTrigger, setWordInfo] = useStateTrigger(null)

  const score = useMemo(() => {
    let total = 0
    for (const word of foundWords) total += getWordScore(word)
    return total
  }, [foundWords])

  // board e palavras disponíveis no dia
  const board = useMemo(() => generateTodaysBoard(), [])
  const allWords = useMemo(() => findAllWords(board), [board])

  const levels = useMemo(() => {
    let maxScore = 0
    for (const word of allWords) maxScore += getWordScore(word)
    return [
      0,
      Math.floor(maxScore * 0.05),
      Math.floor(maxScore * 0.11),
      Math.floor(maxScore * 0.18),
      Math.floor(maxScore * 0.26),
      Math.floor(maxScore * 0.35),
      Math.floor(maxScore * 0.45),
      Math.floor(maxScore * 0.56),
      Math.floor(maxScore * 0.68)
    ]
  }, [allWords])

  // submit da palavra atual
  const submitWord = () => {
    const newWord = currentWordRef.current
    if (!newWord) return

    if (newWord.length === 1) setWordInfo(null)
    else if (newWord.length < minWordLength) {
      setWordInfo('Palavra muito pequena')
    } else if (allWords.find(w => w.word === newWord)) {
      const foundWordsCopy = [...foundWordsRef.current]
      foundWordsCopy.push(newWord)
      setFoundWords(foundWordsCopy)
      setWordInfo('Boa!')
    } else {
      setWordInfo('Não é uma palavra desse jogo')
      onError && onError()
    }

    setWordActive(false)
    setCurrentWord('')
    setPath([])
  }

  // mouseup global para submeter palavra
  const handleMouseUp = e => {
    if (e.button !== 0) return
    submitWord()
  }

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // popup de info de palavra
  useEffect(() => {
    if (wordInfo === null) return
    const timeout = setTimeout(() => setWordInfo(null), 1500)
    return () => clearTimeout(timeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordInfoTrigger])

  // square info
  const getSquareInfo = (x, y) => {
    let active = false
    let lineDirection = null

    for (let i = 0; i < path.length; i++) {
      if (path[i].x === x && path[i].y === y) {
        active = true
        if (i < path.length - 1) {
          lineDirection = {
            x: path[i + 1].x - path[i].x,
            y: path[i + 1].y - path[i].y
          }
        }
      }
    }
    return {
      letter: board[x][y],
      active,
      lineDirection
    }
  }

  // helpers de path
  const addToPath = (x, y) => {
    const pathCopy = [...path, { x, y }]
    setPath(pathCopy)
    const newLetter = board[x][y]
    setCurrentWord(currentWord + newLetter)
  }

  const removeFromPath = () => {
    if (path.length === 0) return
    const pathCopy = path.slice(0, -1)
    setPath(pathCopy)
    setCurrentWord(currentWord.slice(0, -1))
  }

  // eventos
  const squareClicked = (x, y) => {
    if (wordActive) return
    setWordActive(true)
    addToPath(x, y)
  }

  const squareEntered = (x, y) => {
    if (!wordActive) return
    for (let i = 0; i < path.length; i++) {
      if (path[i].x === x && path[i].y === y) {
        if (i === path.length - 2) removeFromPath()
        return
      }
    }
    const { x: px, y: py } = path[path.length - 1] || {}
    if (px === undefined) return
    if (Math.abs(x - px) > 1 || Math.abs(y - py) > 1) return
    addToPath(x, y)
  }

  return (
    <GameContext.Provider
      value={{
        currentWord,
        getSquareInfo,
        squareClicked,
        squareEntered,
        submitWord,
        allWords,
        foundWords,
        score,
        levels,
        wordInfo,
        wordsList,
        onComplete,
        onError,
        ...rest
      }}
    >
      {children}
    </GameContext.Provider>
  )
}

const useGame = () => {
  const context = useContext(GameContext)
  if (context === undefined)
    throw new Error('useGame must be used inside a GameProvider')
  return context
}

export { useGame, GameProvider }
